import "@testing-library/jest-dom";
import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import { MemoryRouter, Route, Routes } from "react-router-dom";
import ViewBuilder from "./ViewBuilder";

const mockUpdateView = jest.fn();

const mockColumn = (id: string, name: string, pk = false) => ({
  id,
  table_id: "t1",
  column_name: name,
  display_name: name,
  data_type: "NVARCHAR(255)",
  is_primary_key: pk,
  is_foreign_key: false,
  is_nullable: true,
});

const mockSchemas = [
  {
    id: "t1",
    schema_name: "data",
    table_name: "people",
    display_name: "People",
    primary_key_column: "id",
    columns: [
      mockColumn("c-id", "id", true),
      mockColumn("c-name", "name"),
      mockColumn("c-email", "email"),
    ],
  },
];

// What GET /views/{id}/full returns (field and permission keys as the API names them).
const mockView = {
  id: "v1",
  workspace_id: "ws",
  name: "People view",
  description: "",
  focus_table_id: "t1",
  fields: [
    {
      id: "f2",
      view_id: "v1",
      column_id: "c-email",
      display_label: "Work email",
      display_order: 1,
      is_visible: false,
      is_editable: false,
      is_link_field: true,
    },
    {
      id: "f1",
      view_id: "v1",
      column_id: "c-name",
      display_label: "Full name",
      display_order: 0,
      is_visible: true,
      is_editable: true,
      is_link_field: false,
    },
  ],
  joins: [],
  filters: [],
  default_filters: [
    {
      id: "df1",
      view_id: "v1",
      column_id: "c-email",
      operator: "ne",
      value_json: {
        value: "closed@example.com",
        source_table_alias: null as string | null,
      },
      is_locked: false,
    },
  ],
  permissions: [
    {
      id: "p1",
      view_id: "v1",
      group_id: "g-hr",
      can_view: true,
      can_edit: true,
      can_delete: false,
      can_export: true,
    },
    {
      id: "p2",
      view_id: "v1",
      group_id: "g-staff",
      can_view: true,
      can_edit: false,
      can_delete: false,
      can_export: false,
    },
  ],
};

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "ws" } }),
}));
jest.mock("../hooks/useViews", () => ({
  useView: () => ({ view: mockView, loading: false }),
  useViewMutations: () => ({
    createView: jest.fn(),
    updateView: mockUpdateView,
    loading: false,
  }),
}));
jest.mock("../hooks/useSchemas", () => ({
  useSchemas: () => ({ schemas: mockSchemas, loading: false }),
}));
jest.mock("../components/viewBuilder/steps/FocusTableStep", () => () => null);
jest.mock("../components/viewBuilder/steps/JoinsStep", () => () => null);
jest.mock("../components/viewBuilder/steps/FieldsStep", () => () => null);
jest.mock("../components/viewBuilder/steps/FiltersStep", () => () => null);
jest.mock("../components/viewBuilder/steps/PermissionsStep", () => () => null);
jest.mock("../components/MyVaultAgent", () => ({
  FloatingChatWidget: () => null,
}));

async function saveWithoutTouchingAnything() {
  render(
    <MemoryRouter initialEntries={["/views/v1/edit"]}>
      <Routes>
        <Route path="/views/:id/edit" element={<ViewBuilder />} />
        <Route path="*" element={<div>after save</div>} />
      </Routes>
    </MemoryRouter>,
  );
  for (let step = 0; step < 4; step += 1) {
    fireEvent.click(await screen.findByRole("button", { name: /^next$/i }));
  }
  fireEvent.click(await screen.findByRole("button", { name: /update view/i }));
  await waitFor(() => expect(mockUpdateView).toHaveBeenCalled());
  return mockUpdateView.mock.calls[0][1];
}

describe("ViewBuilder edit", () => {
  beforeEach(() => {
    mockUpdateView.mockReset();
    mockUpdateView.mockResolvedValue({});
  });

  it("sends the view's existing grants back instead of replacing them with none", async () => {
    const payload = await saveWithoutTouchingAnything();

    expect(payload.permissions).toEqual([
      expect.objectContaining({
        groupId: "g-hr",
        canView: true,
        canEdit: true,
        canDelete: false,
        canExport: true,
      }),
      expect.objectContaining({
        groupId: "g-staff",
        canView: true,
        canEdit: false,
        canDelete: false,
        canExport: false,
      }),
    ]);
  });

  it("keeps stored field order and settings when the API returns fields out of order", async () => {
    const payload = await saveWithoutTouchingAnything();

    expect(payload.fields).toEqual([
      expect.objectContaining({
        columnId: "c-name",
        displayLabel: "Full name",
        displayOrder: 0,
        isVisible: true,
        isEditable: true,
        isLinkField: false,
      }),
      expect.objectContaining({
        columnId: "c-email",
        displayLabel: "Work email",
        displayOrder: 1,
        isVisible: false,
        isEditable: false,
        isLinkField: true,
      }),
    ]);
  });

  it("sends the persisted default filters back when the view is saved", async () => {
    const payload = await saveWithoutTouchingAnything();

    expect(payload.default_filters).toEqual([
      {
        field: "email",
        operator: "ne",
        value: "closed@example.com",
      },
    ]);
  });

  it("round-trips the source alias when the same column is selected twice", async () => {
    const originalFields = mockView.fields;
    const originalFilters = mockView.default_filters;
    const reviewerField = Object.assign({}, originalFields[0], {
      source_table_alias: "reviewer",
      display_order: 0,
    });
    const managerField = Object.assign({}, originalFields[0], {
      id: "f-manager-email",
      source_table_alias: "manager",
      display_order: 1,
    });
    mockView.fields = [reviewerField, managerField, originalFields[1]];
    mockView.default_filters = [
      {
        ...originalFilters[0],
        value_json: {
          value: "closed@example.com",
          source_table_alias: "reviewer",
        },
      },
    ];

    try {
      const payload = await saveWithoutTouchingAnything();
      expect(payload.default_filters).toEqual([
        {
          field: "reviewer.email",
          operator: "ne",
          value: "closed@example.com",
        },
      ]);
    } finally {
      mockView.fields = originalFields;
      mockView.default_filters = originalFilters;
    }
  });
});
